var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useOkapiKy } from "@folio/stripes/core";
import { useIntl } from "react-intl";
import { useMutation } from "react-query";
import { assignProductNumber, previewProductNumberAssignment, deselectProductNumber, } from "../api/productNumberService";
import { handlePublisherPrefixRangeError, formatModalErrors, } from "../utils/publisherPrefixRangeErrorHandler";
import { ErrorType } from "../types/ErrorType";
import { getErrorCode } from "../utils/getErrorCode";
import { MAGNITUDE_NOT_ALLOWED_MESSAGE_ID, MIN_NEW_PREFIX_MAGNITUDE, NO_NUMBERS_MESSAGE_ID, resolveAssignErrorMessageId, } from "../utils/newPrefixMagnitude";
import { getAssignNoticeMessageId } from "../utils/productNumberAssignErrors";
/**
 * Whether the assignment preview can be confirmed (RCK-1685): it drew a number, or the assignment creates a new
 * publisher prefix - the backend preview always says so ({@code createNewPublisherPrefix}) when its decision proposes
 * one, and an assignment creates a prefix only on that decision.
 */
export var canConfirmPreview = function (preview) { var _a; return !!((_a = preview.productNumber) === null || _a === void 0 ? void 0 : _a.id) || !!preview.createNewPublisherPrefix; };
export var useProductNumberAssignActions = function (productId, publisherId, previewResponse, setPreviewResponse, openModal, openSuccessModal, newPrefixNeeded) {
    if (newPrefixNeeded === void 0) { newPrefixNeeded = false; }
    var ky = useOkapiKy();
    var formatMessage = useIntl().formatMessage;
    var history = useHistory();
    var _a = useState(false), showConfirmation = _a[0], setShowConfirmation = _a[1];
    var _b = useState(null), formDataToSubmit = _b[0], setFormDataToSubmit = _b[1];
    // The preview step reserves the number (selected = true) server-side. If the
    // user abandons the flow (cancels the confirmation or navigates away) that
    // reservation must be released, otherwise the number stays selected and the
    // "next available number" lookup skips it, leaving a gap in the sequence.
    // We mirror the latest reserved id into a ref so the unmount cleanup can read
    // it without capturing a stale render value.
    var reservedProductNumberIdRef = useRef(null);
    var assignedRef = useRef(false);
    useEffect(function () {
        var _a, _b;
        reservedProductNumberIdRef.current =
            (_b = (_a = previewResponse === null || previewResponse === void 0 ? void 0 : previewResponse.productNumber) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
    }, [previewResponse]);
    var releaseReservation = function () {
        var reservedId = reservedProductNumberIdRef.current;
        if (!reservedId || assignedRef.current) {
            return;
        }
        reservedProductNumberIdRef.current = null;
        // Best-effort cleanup: a failure here is covered by the server-side
        // scheduled safety-net, so we log rather than surface it to the user.
        deselectProductNumber(ky, reservedId).catch(function (error) {
            return console.error("Failed to release reserved product number:", error);
        });
    };
    useEffect(function () {
        return function () {
            releaseReservation();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var previewMutation = useMutation(function (previewData) { return previewProductNumberAssignment(ky, previewData); }, {
        onSuccess: function (preview) {
            var _a;
            // RCK-1685: confirm only a drawn number or the creation of a new prefix; a preview without either would
            // confirm nothing ("Přidělené číslo: -") and the assignment would fail - say why instead, and keep no part
            // of it (its prefix) for a later submit.
            if (canConfirmPreview(preview)) {
                setPreviewResponse(preview);
                setShowConfirmation(true);
                return;
            }
            // The server released the previously previewed number already (it deselects it before each preview), so it
            // is only forgotten here - a client release could free another user's new reservation of it.
            reservedProductNumberIdRef.current = null;
            setPreviewResponse(null);
            setFormDataToSubmit(null);
            openModal(formatMessage({
                id: (_a = getAssignNoticeMessageId(preview)) !== null && _a !== void 0 ? _a : NO_NUMBERS_MESSAGE_ID,
            }));
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var errorMessage, apiError, errorData, modalErrors, jsonParseError_1, errorText, _a;
            var _b, _c, _d, _e;
            return __generator(this, function (_f) {
                switch (_f.label) {
                    case 0:
                        console.error("Error previewing product number:", error);
                        errorMessage = formatMessage({
                            id: "ui-br-agency.product-number.assign.error.previewFailed",
                        });
                        apiError = error;
                        if (!apiError.response) return [3 /*break*/, 9];
                        _f.label = 1;
                    case 1:
                        _f.trys.push([1, 3, , 8]);
                        return [4 /*yield*/, apiError.response.json()];
                    case 2:
                        errorData = _f.sent();
                        if (((_c = (_b = errorData === null || errorData === void 0 ? void 0 : errorData.errors) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.type) ===
                            ErrorType.PUBLISHER_PREFIX_MAGNITUDE_NOT_ALLOWED) {
                            // FSD PP20: the chosen block size exceeds what the publisher may get.
                            errorMessage = formatMessage({
                                id: MAGNITUDE_NOT_ALLOWED_MESSAGE_ID,
                            });
                        }
                        else if ((errorData === null || errorData === void 0 ? void 0 : errorData.errors) && Array.isArray(errorData.errors)) {
                            modalErrors = handlePublisherPrefixRangeError(errorData, formatMessage).modalErrors;
                            if (modalErrors.length > 0) {
                                errorMessage = formatModalErrors(modalErrors);
                            }
                        }
                        else if ((_e = (_d = errorData === null || errorData === void 0 ? void 0 : errorData.errors) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.message) {
                            errorMessage = errorData.errors[0].message;
                        }
                        else if (errorData === null || errorData === void 0 ? void 0 : errorData.message) {
                            errorMessage = errorData.message;
                        }
                        return [3 /*break*/, 8];
                    case 3:
                        jsonParseError_1 = _f.sent();
                        _f.label = 4;
                    case 4:
                        _f.trys.push([4, 6, , 7]);
                        return [4 /*yield*/, apiError.response.text()];
                    case 5:
                        errorText = _f.sent();
                        if (errorText) {
                            errorMessage = errorText;
                        }
                        return [3 /*break*/, 7];
                    case 6:
                        _a = _f.sent();
                        return [3 /*break*/, 7];
                    case 7: return [3 /*break*/, 8];
                    case 8: return [3 /*break*/, 10];
                    case 9:
                        errorMessage = error.message;
                        _f.label = 10;
                    case 10:
                        openModal(errorMessage);
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var assignMutation = useMutation(function (assignPayload) {
        return assignProductNumber(ky, assignPayload);
    }, {
        onSuccess: function (result) {
            assignedRef.current = true;
            reservedProductNumberIdRef.current = null;
            setShowConfirmation(false);
            openSuccessModal(formatMessage({ id: "ui-br-agency.product-number.assign.success.assigned" }, {
                value: result.fullFormattedValue ||
                    result.formattedValue ||
                    result.value ||
                    (previewResponse === null || previewResponse === void 0 ? void 0 : previewResponse.assignedValue) ||
                    "",
            }), function () { return __awaiter(void 0, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    history.push("/bragency/products?detail=".concat(productId));
                    return [2 /*return*/];
                });
            }); });
            setFormDataToSubmit(null);
            setPreviewResponse(null);
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var errorMessage, _a, _b;
            var _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        console.error("Error assigning product number:", error);
                        setShowConfirmation(false);
                        _a = formatMessage;
                        _c = {};
                        _b = resolveAssignErrorMessageId;
                        return [4 /*yield*/, getErrorCode(error)];
                    case 1:
                        errorMessage = _a.apply(void 0, [(_c.id = _b.apply(void 0, [_d.sent(), "ui-br-agency.product-number.assign.error.assignFailed"]),
                                _c)]);
                        openModal(errorMessage);
                        // The failed assignment leaves the previewed number reserved — release it
                        // before clearing the preview so it does not become an orphaned gap.
                        releaseReservation();
                        setFormDataToSubmit(null);
                        setPreviewResponse(null);
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var buildPayload = function (formData, forPreview) {
        var _a, _b, _c, _d;
        var payload = {
            productId: productId,
            publisherId: publisherId,
            magnitude: forPreview
                ? 0
                : ((_a = formData.magnitude) !== null && _a !== void 0 ? _a : MIN_NEW_PREFIX_MAGNITUDE),
        };
        if ((_b = previewResponse === null || previewResponse === void 0 ? void 0 : previewResponse.productNumber) === null || _b === void 0 ? void 0 : _b.id) {
            payload.productNumberId = previewResponse.productNumber.id;
        }
        if (formData.agencyPrefixId) {
            payload.agencyPrefixId = formData.agencyPrefixId;
        }
        else if ((_c = previewResponse === null || previewResponse === void 0 ? void 0 : previewResponse.agencyPrefix) === null || _c === void 0 ? void 0 : _c.id) {
            payload.agencyPrefixId = previewResponse.agencyPrefix.id;
        }
        if (newPrefixNeeded || (previewResponse === null || previewResponse === void 0 ? void 0 : previewResponse.createNewPublisherPrefix)) {
            return payload;
        }
        if (formData.publisherPrefixId) {
            payload.publisherPrefixId = formData.publisherPrefixId;
        }
        else if ((_d = previewResponse === null || previewResponse === void 0 ? void 0 : previewResponse.publisherPrefix) === null || _d === void 0 ? void 0 : _d.id) {
            payload.publisherPrefixId = previewResponse.publisherPrefix.id;
        }
        return payload;
    };
    var onSubmit = function (formData) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (!publisherId)
                return [2 /*return*/];
            assignedRef.current = false;
            setFormDataToSubmit(formData);
            previewMutation.mutate(buildPayload(formData, true));
            return [2 /*return*/];
        });
    }); };
    var handleConfirmSubmit = function () {
        if (!showConfirmation ||
            !formDataToSubmit ||
            !publisherId ||
            !previewResponse) {
            return;
        }
        assignMutation.mutate(buildPayload(formDataToSubmit, false));
    };
    var handleCancelConfirmation = function () {
        // Cancelling the confirmation abandons the previewed number — release the
        // reservation so it stays available for the next assignment.
        releaseReservation();
        setShowConfirmation(false);
        setFormDataToSubmit(null);
        setPreviewResponse(null);
    };
    return {
        showConfirmation: showConfirmation,
        formDataToSubmit: formDataToSubmit,
        onSubmit: onSubmit,
        handleConfirmSubmit: handleConfirmSubmit,
        handleCancelConfirmation: handleCancelConfirmation,
    };
};
